
import { useState } from "react";
import { StoreEmployee } from "@/models/storeModel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { Trash2 } from "lucide-react";
import { ConfirmDeleteDialog } from "@/components/tables/ConfirmDeleteDialog";

interface EmployeeTableProps {
  employees: StoreEmployee[];
  canManageEmployees: boolean;
  onRoleChange: (employeeId: string, role: 'admin' | 'manager' | 'staff' | 'super_admin') => Promise<void>;
  onDeleteEmployee: (employee: StoreEmployee) => void;
  showStoreColumn?: boolean;
}

export function EmployeeTable({ 
  employees,
  canManageEmployees,
  onRoleChange,
  onDeleteEmployee,
  showStoreColumn = false
}: EmployeeTableProps) {
  const { toast } = useToast();
  const { stores } = useStoreManagementStore();
  const [updatingIds, setUpdatingIds] = useState<string[]>([]);
  const [employeeToDelete, setEmployeeToDelete] = useState<StoreEmployee | null>(null);

  // Get store name for display
  const getStoreName = (storeId: string) => {
    return stores.find(store => store.id === storeId)?.name || 'Unknown Store';
  };

  const handleRoleChange = async (employeeId: string, role: 'admin' | 'manager' | 'staff' | 'super_admin') => {
    // Add employee ID to updating state
    setUpdatingIds((prev) => [...prev, employeeId]);
    
    try {
      await onRoleChange(employeeId, role);
      toast({
        title: "Role updated",
        description: "Employee role has been updated successfully."
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update employee role.",
        variant: "destructive"
      });
    } finally {
      // Remove employee ID from updating state
      setUpdatingIds((prev) => prev.filter(id => id !== employeeId));
    }
  };

  const handleDeleteClick = (employee: StoreEmployee) => {
    setEmployeeToDelete(employee);
  };

  const confirmDelete = () => {
    if (employeeToDelete) {
      onDeleteEmployee(employeeToDelete);
      setEmployeeToDelete(null);
    }
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Email</TableHead>
            {showStoreColumn && <TableHead>Store</TableHead>}
            <TableHead>Since</TableHead>
            <TableHead>Address</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {employees.map((employee) => (
            <TableRow key={employee.id}>
              <TableCell>{`${employee.firstName} ${employee.lastName}`}</TableCell>
              <TableCell>
                {canManageEmployees ? (
                  <Select
                    defaultValue={employee.role}
                    onValueChange={(value) => 
                      handleRoleChange(
                        employee.id, 
                        value as 'admin' | 'manager' | 'staff' | 'super_admin'
                      )
                    }
                    disabled={updatingIds.includes(employee.id)}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="staff">Staff</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant="outline">
                    {employee.role.charAt(0).toUpperCase() + employee.role.slice(1)}
                  </Badge>
                )}
              </TableCell>
              <TableCell>{employee.email || 'N/A'}</TableCell>
              {showStoreColumn && (
                <TableCell>{getStoreName(employee.storeId)}</TableCell>
              )}
              <TableCell>
                {new Date(employee.createdAt).toLocaleDateString()}
              </TableCell>
              <TableCell>
                {employee.address || 'N/A'}
              </TableCell>
              <TableCell className="text-right">
                {canManageEmployees && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteClick(employee)}
                    className="text-red-500 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {employeeToDelete && (
        <ConfirmDeleteDialog
          open={!!employeeToDelete}
          onOpenChange={(open) => !open && setEmployeeToDelete(null)}
          onConfirm={confirmDelete}
          title="Remove Employee"
          description={`Are you sure you want to remove ${employeeToDelete.firstName} ${employeeToDelete.lastName}? This action cannot be undone.`}
        />
      )}
    </>
  );
}
